/* Asks before a Druid transforms: shows the form and what transforming costs, as the
   class text words it: a Stress (Beastform), or Hope instead (the Evolution Hope feature).
   Opened without a form, it first lists the forms of the Druid's tier or lower. */

import { useState } from 'react';
import type { Catalog } from './catalog';
import { classFeatures } from './catalog';
import { attackLine, EVOLUTION_HOPE, evolvedTiers, extraStress, formStats } from './beastform';
import { Character, deriveStats, markCount, transform, Trait, TRAITS } from './model';
import { tierOf } from './leveling';
import { capitalize } from './SheetParts';
import { Chips, Panel } from './BuilderParts';
import Dialog from './Dialog';

interface Props {
  character: Character;
  catalog: Catalog;
  /** Empty to choose the form first. */
  form: string;
  onClose: () => void;
  onDone: (c: Character) => void;
}

export default function BeastformDialog({ character: c, catalog, form: picked, onClose, onDone }: Props) {
  const [name, setName] = useState(picked);
  const [base, setBase] = useState('');
  const [raise, setRaise] = useState<Trait | ''>('');
  const form = catalog.beastforms.find((f) => f.name === name);
  const cls = catalog.cls(c.className);
  const stats = deriveStats(c, catalog);
  const tiers = evolvedTiers(form);
  const active = tiers.length ? { name, base } : { name };
  const preview = formStats(active, catalog);
  const extra = extraStress(form);
  const openStress = stats.stress - markCount(c, 'stress');
  const hope = markCount(c, 'hope');
  const rule = classFeatures(cls).find((f) => f.name === 'Beastform')?.description?.split('. ')[0];
  const evolution = cls?.hopeFeature?.startsWith('Evolution') ? cls.hopeFeature.replace(/^Evolution:\s*/, '') : '';

  const cancel = <button type="button" className="cb-btn" onClick={onClose}>Cancel</button>;

  if (!form) {
    const yourTiers = [1, 2, 3, 4].filter((t) => t <= tierOf(stats.level));
    return (
      <Dialog title="Choose a Beastform" onClose={onClose} footer={cancel}>
        <p>{rule}.</p>
        <div role="group" aria-label="Beastforms">
          {yourTiers.map((t) => (
            <div key={t}>
              <p className="cb-field__label">Tier {t}</p>
              <Chips options={catalog.beastforms.filter((f) => f.tier === t).map((f) => f.name)} isOn={() => false} onPick={setName} />
            </div>
          ))}
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog title={`Transform into ${name}?`} onClose={onClose} footer={cancel}>
      <p className="cb-hint">Tier {form.tier} ({form.examples})</p>
      {tiers.length > 0 && (
        <label className="cb-field">
          <span className="cb-field__label">Upgrade which form?</span>
          <select value={base} onChange={(e) => setBase(e.target.value)}>
            <option value="">Choose a Tier {tiers.join(' or ')} form</option>
            {catalog.beastforms.filter((f) => tiers.includes(f.tier)).map((f) => <option key={f.name} value={f.name}>{f.name}</option>)}
          </select>
        </label>
      )}
      {preview && (
        <p className="cb-beast-stats">
          {capitalize(preview.trait)} +{preview.traitBonus} · Evasion +{preview.evasionBonus} · {attackLine(preview, stats.proficiency)}
          {preview.advantages.length > 0 && <><br />Advantage on {preview.advantages.join(', ')}</>}
        </p>
      )}
      {form.features.map((f) => <p key={f.name}><strong>{f.name}:</strong> {f.text}</p>)}

      <Panel title="Beastform">
        <p>{rule}.</p>
        <button
          type="button"
          className="cb-btn cb-btn--primary"
          disabled={!preview || openStress < 1 + extra}
          onClick={() => onDone(transform(c, catalog, active, 'stress'))}
        >
          {extra ? `Mark ${1 + extra} Stress and transform` : 'Mark a Stress and transform'}
        </button>
        {openStress < 1 + extra && <p className="cb-hint">You have {openStress} open Stress slots.</p>}
      </Panel>

      {evolution && (
        <Panel title="Evolution (Hope feature)">
          <p>{evolution}</p>
          <Chips label="Trait to raise" options={TRAITS.map(capitalize)} isOn={(t) => t.toLowerCase() === raise} onPick={(t) => setRaise(t.toLowerCase() as Trait)} />
          <button
            type="button"
            className="cb-btn cb-btn--primary"
            disabled={!preview || !raise || hope < EVOLUTION_HOPE || openStress < extra}
            onClick={() => raise && onDone(transform(c, catalog, { ...active, evolution: raise }, 'hope'))}
          >
            {extra ? `Spend ${EVOLUTION_HOPE} Hope, mark ${extra} Stress and transform` : `Spend ${EVOLUTION_HOPE} Hope and transform`}
          </button>
          {hope < EVOLUTION_HOPE && <p className="cb-hint">You have {hope} Hope.</p>}
        </Panel>
      )}
    </Dialog>
  );
}
